// ─────────────────────────────────────────────────────────────
// UserSearchDropdown
//
// A paginated, infinitely-scrollable user picker for admin contexts.
// Searches by name or email via the admin user search endpoint,
// accumulates results as the user scrolls, and debounces input.
//
// Usage:
//   <UserSearchDropdown value={selectedUser} onChange={setSelectedUser} />
// ─────────────────────────────────────────────────────────────
import { useState, useRef, useEffect, useCallback } from 'react'
import { useSearchAdminUsersQuery } from '../../store/api/agentsApi'
import Avatar from './Avatar'

export interface UserSearchOption {
  id: string
  displayName: string
  email: string
  avatarUrl: string | null
}

interface UserSearchDropdownProps {
  value: UserSearchOption | null
  onChange: (user: UserSearchOption | null) => void
  placeholder?: string
  minWidth?: string | number
}

export function UserSearchDropdown({
  value,
  onChange,
  placeholder = 'All users',
  minWidth = '200px',
}: UserSearchDropdownProps) {
  const [open, setOpen]             = useState(false)
  const [query, setQuery]           = useState('')
  const [cursor, setCursor]         = useState<string | undefined>(undefined)
  const [options, setOptions]       = useState<UserSearchOption[]>([])
  const [hasMore, setHasMore]       = useState(false)
  const [debouncedQ, setDebouncedQ] = useState('')
  const debounceRef                 = useRef<ReturnType<typeof setTimeout> | null>(null)
  const listRef                     = useRef<HTMLDivElement>(null)
  const wrapRef                     = useRef<HTMLDivElement>(null)
  const prevCursorRef               = useRef<string | undefined>(undefined)

  // Debounce the search query
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current)
    debounceRef.current = setTimeout(() => {
      setDebouncedQ(query)
      setCursor(undefined)
      prevCursorRef.current = undefined
      setOptions([])
    }, 300)
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current) }
  }, [query])

  const { data, isFetching } = useSearchAdminUsersQuery(
    { q: debouncedQ, cursor },
    { skip: !open },
  )

  // Accumulate pages as cursor advances
  useEffect(() => {
    if (!data) return
    if (prevCursorRef.current === undefined && cursor === undefined) {
      setOptions(data.data)
    } else {
      setOptions(prev => {
        const existingIds = new Set(prev.map(u => u.id))
        return [...prev, ...data.data.filter(u => !existingIds.has(u.id))]
      })
    }
    setHasMore(data.hasMore)
  }, [data]) // eslint-disable-line react-hooks/exhaustive-deps

  // Infinite scroll within the dropdown list
  const handleScroll = useCallback(() => {
    const el = listRef.current
    if (!el || isFetching || !hasMore) return
    if (el.scrollTop + el.clientHeight >= el.scrollHeight - 20) {
      const nextCursor = data?.nextCursor ?? undefined
      prevCursorRef.current = nextCursor
      setCursor(nextCursor)
    }
  }, [isFetching, hasMore, data])

  // Close on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [])

  const handleOpen = () => {
    setOpen(o => !o)
    if (!open) {
      setQuery('')
      setCursor(undefined)
      prevCursorRef.current = undefined
      setOptions([])
    }
  }

  const handleSelect = (user: UserSearchOption | null) => {
    onChange(user)
    setOpen(false)
  }

  return (
    <div ref={wrapRef} style={{ position: 'relative', minWidth }}>
      {/* Trigger */}
      <div
        onClick={handleOpen}
        style={{
          display: 'flex', alignItems: 'center', gap: '0.4rem',
          padding: '0.3rem 0.65rem', borderRadius: 6, fontSize: '0.75rem',
          background: 'var(--bg-elevated)',
          border: `1px solid ${value ? 'var(--accent-primary)' : 'var(--border)'}`,
          color: value ? 'var(--text-primary)' : 'var(--text-muted)',
          cursor: 'pointer', minWidth: '180px',
        }}
      >
        {value ? (
          <>
            <Avatar name={value.displayName} avatarUrl={value.avatarUrl} size={18} />
            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '140px' }}>
              {value.displayName}
            </span>
          </>
        ) : (
          <span>{placeholder}</span>
        )}
        <span style={{ marginLeft: 'auto', color: 'var(--text-muted)', fontSize: '0.65rem' }}>▾</span>
        {value && (
          <span
            onClick={e => { e.stopPropagation(); onChange(null) }}
            style={{ marginLeft: '0.25rem', color: 'var(--text-muted)', fontWeight: 700, fontSize: '0.8rem', lineHeight: 1 }}
          >
            ✕
          </span>
        )}
      </div>

      {/* Dropdown panel */}
      {open && (
        <div style={{
          position: 'absolute', top: 'calc(100% + 4px)', left: 0, zIndex: 100,
          width: '260px', background: 'var(--bg-card)', border: '1px solid var(--border)',
          borderRadius: 8, boxShadow: '0 8px 24px rgba(0,0,0,0.3)', overflow: 'hidden',
        }}>
          <div style={{ padding: '0.4rem' }}>
            <input
              autoFocus
              value={query}
              onChange={e => setQuery(e.target.value)}
              placeholder="Search by name or email…"
              style={{
                width: '100%', padding: '0.35rem 0.6rem', borderRadius: 6, fontSize: '0.75rem',
                background: 'var(--bg-elevated)', border: '1px solid var(--border)',
                color: 'var(--text-primary)', outline: 'none', boxSizing: 'border-box',
              }}
            />
          </div>

          <div ref={listRef} onScroll={handleScroll} style={{ maxHeight: '220px', overflowY: 'auto' }}>
            {/* "All users" option */}
            <div
              onClick={() => handleSelect(null)}
              style={{
                display: 'flex', alignItems: 'center', gap: '0.5rem',
                padding: '0.45rem 0.75rem', cursor: 'pointer', fontSize: '0.78rem',
                color: !value ? 'var(--accent-primary)' : 'var(--text-secondary)',
                background: !value ? 'rgba(124,58,237,0.08)' : 'transparent',
              }}
            >
              <span style={{ fontSize: '1rem' }}>👥</span> {placeholder}
            </div>

            {options.map(u => (
              <div
                key={u.id}
                onClick={() => handleSelect(u)}
                style={{
                  display: 'flex', alignItems: 'center', gap: '0.5rem',
                  padding: '0.45rem 0.75rem', cursor: 'pointer',
                  background: value?.id === u.id ? 'rgba(124,58,237,0.08)' : 'transparent',
                }}
                onMouseEnter={e => { if (value?.id !== u.id) (e.currentTarget as HTMLElement).style.background = 'var(--bg-elevated)' }}
                onMouseLeave={e => { if (value?.id !== u.id) (e.currentTarget as HTMLElement).style.background = 'transparent' }}
              >
                <Avatar name={u.displayName} avatarUrl={u.avatarUrl} size={24} />
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontSize: '0.78rem', fontWeight: 600, color: 'var(--text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {u.displayName}
                  </div>
                  <div style={{ fontSize: '0.67rem', color: 'var(--text-muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {u.email}
                  </div>
                </div>
              </div>
            ))}

            {isFetching && (
              <div style={{ padding: '0.5rem', textAlign: 'center', fontSize: '0.72rem', color: 'var(--text-muted)' }}>
                Loading…
              </div>
            )}
            {!isFetching && options.length === 0 && query && (
              <div style={{ padding: '0.5rem', textAlign: 'center', fontSize: '0.72rem', color: 'var(--text-muted)' }}>
                No users found
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  )
}
